import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { DataGridPro, GridToolbarContainer, GridColDef, GridPaginationModel, GridSortModel, GridFilterModel, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from "../AxiosInstance";

// Type definitions
interface RetroTrigger {
    retroTriggerId: number;
    weekStartDate: string;
    customerName: string;
    skuNumber: string;
    units: number;
    triggerValue: number;
    createdUtc: string;
    updatedUtc: string;
}

interface RetroTriggersResponse {
    retroTriggers: RetroTrigger[];
    totalCount: number;
}

interface CustomToolbarProps {
    selectedRows: GridRowSelectionModel;
    onDelete: () => void;
}

const formatDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
    });
};

const formatQuantity = (value: number): string => {
    if (value === 0) return '-';
    return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDecimal = (value: number): string => {
    if (value === 0) return '-';
    return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

function CustomToolbar({ selectedRows, onDelete }: CustomToolbarProps) {
    return (
        <GridToolbarContainer>
            {selectedRows.length > 0 && (
                <Button
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                    color="error"
                    size="small"
                >
                    Delete Selected ({selectedRows.length})
                </Button>
            )}
        </GridToolbarContainer>
    );
}

function RetroTriggersGrid() {
    const [retroTriggers, setRetroTriggers] = useState<RetroTrigger[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 20,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'weekStartDate',
            sort: 'desc',
        },
    ]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
        quickFilterValues: [],
    });
    const [rowCount, setRowCount] = useState<number>(0);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [rowsToDelete, setRowsToDelete] = useState<GridRowSelectionModel>([]);

    const getToken = (): string | null => localStorage.getItem("token");

    const fetchRetroTriggers = async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            
            const token = getToken();
            
            // Prepare pagination parameters
            const params: Record<string, string | number> = {
                page: paginationModel.page + 1, // API expects 1-based page numbers
                pageSize: paginationModel.pageSize
            };

            // Add sorting parameters
            if (sortModel.length > 0) {
                params.sortField = sortModel[0].field;
                params.sortDirection = sortModel[0].sort as string;
            }

            // Add filter parameters
            if (filterModel.quickFilterValues && filterModel.quickFilterValues.length > 0) {
                params.search = filterModel.quickFilterValues.join(' ');
            }
            
            const response = await axiosInstance.get<RetroTriggersResponse>('/retrotriggers', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params
            });
            
            setRetroTriggers(response.data.retroTriggers || []);
            setRowCount(response.data.totalCount || 0);
        } catch (error) {
            console.error("Failed to fetch retro triggers:", error);
            setError("Failed to load retro triggers. Please try again.");
            setRetroTriggers([]);
            setRowCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSelected = (): void => {
        setRowsToDelete(selectedRows);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async (): Promise<void> => {
        try {
            const token = getToken();
            
            if (rowsToDelete.length === 1) {
                await axiosInstance.delete(`/retrotriggers/${rowsToDelete[0]}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
            } else {
                await axiosInstance.post('/retrotriggers/delete-multiple', rowsToDelete, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                });
            }
            
            setDeleteDialogOpen(false);
            setSelectedRows([]);
            setRowsToDelete([]);
            fetchRetroTriggers();
        } catch (error) {
            console.error("Failed to delete retro triggers:", error);
            setError("Failed to delete retro triggers. Please try again.");
            setDeleteDialogOpen(false);
        }
    };

    const cancelDelete = (): void => {
        setDeleteDialogOpen(false);
        setRowsToDelete([]);
    };

    useEffect(() => {
        fetchRetroTriggers();
    }, [paginationModel, sortModel, filterModel]);

    const columns: GridColDef[] = [
        {
            field: 'weekStartDate',
            headerName: 'Week Start Date',
            width: 130,
            type: 'date',
            sortable: true,
            filterable: true,
            valueGetter: (value: string) => value ? new Date(value) : null,
            valueFormatter: (value: Date | null) => value ? formatDate(value) : '',
        },
        {
            field: 'customerName',
            headerName: 'Customer',
            width: 200,
            sortable: true,
            filterable: true,
        },
        {
            field: 'skuNumber',
            headerName: 'SKU',
            width: 150,
            sortable: true,
            filterable: true,
        },
        {
            field: 'units',
            headerName: 'Units',
            width: 100,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            sortable: true,
            filterable: false,
            valueFormatter: (value: number) => formatQuantity(value),
        },
        {
            field: 'triggerValue',
            headerName: 'Trigger Value',
            width: 130,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            sortable: true,
            filterable: false,
            valueFormatter: (value: number) => formatDecimal(value),
        },
        {
            field: 'createdUtc',
            headerName: 'Created',
            width: 150,
            type: 'dateTime',
            sortable: false,
            filterable: false,
            valueGetter: (value: string) => value ? new Date(value) : null,
            valueFormatter: (value: Date | null) => value ? formatDateTime(value.toISOString()) : '-',
        },
        {
            field: 'updatedUtc',
            headerName: 'Updated',
            width: 150,
            type: 'dateTime',
            sortable: false,
            filterable: false,
            valueGetter: (value: string) => value ? new Date(value) : null,
            valueFormatter: (value: Date | null) => value ? formatDateTime(value.toISOString()) : '-',
        },
    ];

    if (loading && retroTriggers.length === 0) {
        return (
            <Box>
                <Skeleton variant="rectangular" width="100%" height={400} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            {retroTriggers.length === 0 && !loading ? (
                <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
                    No records found.
                </Typography>
            ) : (
                <DataGridPro
                    rows={retroTriggers}
                    columns={columns}
                    getRowId={(row) => row.retroTriggerId}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    rowCount={rowCount}
                    loading={loading}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    pageSizeOptions={[10, 20, 50, 100]}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection);
                    }}
                    rowSelectionModel={selectedRows}
                    slots={{
                        toolbar: () => (
                            <CustomToolbar
                                selectedRows={selectedRows}
                                onDelete={handleDeleteSelected}
                            />
                        ),
                    }}
                />
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete {rowsToDelete.length} retro trigger{rowsToDelete.length > 1 ? 's' : ''}? 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default RetroTriggersGrid;

